import { intersection, isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getBillTotal, getFilteredProcedureList, getIsPageNameDetail, getIsPageNameEdit,
  getIsPageNameEditDischarge,
  getIsPageNameReconsider,
  updateHISAdmissionFormValue } from '../../../modules/crm/inPatient/service/crmUtils';
import { RootState } from '../../../store';
import { setAdmissionForm, setCardCollapse, setDynamicQuestionFields, toggleOtherProcedure,
  toggleOtherRoomType, updateAutomatedProceduresOptions } from '../../../store/actions/newAdmissionAction';
import { AdmissionForm } from '../../../store/models/newAdmissionModel';
import {
  getBillDetailFormValue,
  getTreatmentDetailFormValue,
  getOtherMemberDetailFormValues,
  getMemberFormValueFromBenef,
  getMemberFormValueFromClaim,
  getGeneralFormValues,
} from '../../../utils/setClaimDetailInFormUtils';
import { CONCATED_OTHER_TREATMENT_ICDS, MATERNITY_DIAGNOSIS_TYPE, PROCEDURE_OTHER_TYPES } from '../../constants/crmConstants';
import { Payer } from '../../models/payer.model';
import ApplicationDetail from '../../services/applicationDetail.service';
import { useSetFormControlConfig } from '../useSetFormControlConfig';
import { isBefore, isAfter, parseISO, startOfDay, endOfDay} from 'date-fns';

export const useSetDetailInForm = (form?: any, isEditPage?: boolean): any => {
  const [claimDetailBackup, setClaimDetailBackup] = useState<any>({});
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const isHisSearchEnabled = ApplicationDetail.instance.getIsHISSearchEnabled();
  const isNHCXEnabledProvider = ApplicationDetail.instance.getIsNHCXEnabledProvider();
  const getValueBasedOnConfig = useSetFormControlConfig();

  const { payerList: { allPayers, payer, payer: { uniqueIdentifier, isOnMailPayer, isCorporatePayer, isRpaPayer,
    isRPAOutboundDisabledPayer } }, newAdmission: { selectedBeneficiaryDetail = {}, admissionFormVal, cardCollapse, isShowAdmissionForm,
    isDraftSelected, autoFillDayCareProcedure, automatedProcedureOptions, newAdmissionSearchValues, isNewBornCase, defaultClaim },
    admissionDetails: { claimDetails }, reconciliation: {reconciliationClaimDetail},
    payerLookUp, nhcx: {selectedPolicyDetail},
  } = useSelector((state: RootState): RootState => state);

  const {selectedPatientDetail} = useSelector((state: RootState): any => state?.hospital);

  const getIsNormalFLow = (): boolean => {
    return (!isOnMailPayer && !isCorporatePayer && !isRPAOutboundDisabledPayer);
  };

  useEffect((): any => {
    setClaimDetailBackup({});
  }, [payer]);

  useEffect((): void => {
    setClaimDetailBackup(reconciliationClaimDetail);
    form.setFieldsValue(reconciliationClaimDetail);
  }, [reconciliationClaimDetail]);

  useEffect((): void => {
    if (!isEmpty(selectedBeneficiaryDetail)) {
      const formValues = getMemberFormValueFromBenef(admissionFormVal['customerDetail'], allPayers);
      formValues.memberGenderSelect = formValues?.memberGenderSex;
      if (newAdmissionSearchValues) {
        formValues.policyType = newAdmissionSearchValues?.policyType;
      }
      setClaimDetailBackup(formValues);
      form.setFieldsValue(formValues);
    }
  }, [selectedBeneficiaryDetail]);

  const setBooleanInCard = (selectedDiagnosisIds: string[]): void => {
    const isMaternityOpen = !!intersection(selectedDiagnosisIds, MATERNITY_DIAGNOSIS_TYPE).length;
    const isOtherTreatmentDetailOpen = isRpaPayer ? true : !!intersection(selectedDiagnosisIds, CONCATED_OTHER_TREATMENT_ICDS).length;
    const isCovidCaseCardOpen =  (claimDetails?.isCovidCase === 'Yes') ? true : false;

    dispatch(setCardCollapse({ ...cardCollapse, isMaternityOpen, isOtherTreatmentDetailOpen, isCovidCaseCardOpen }));
  };

  useEffect((): any => {
    const isNormalPayer = (isOnMailPayer || isRpaPayer);
    let HISInpatientNo = null;
    if (!isEmpty(selectedPatientDetail)) {
      HISInpatientNo = selectedPatientDetail?.patientDetails?.inpatientNumber;
    }
    if (claimDetails && !isEmpty(claimDetails) && (autoFillDayCareProcedure || isEmpty(selectedBeneficiaryDetail)) && !isDraftSelected &&
     !isNewBornCase && !defaultClaim) {
      const memberFormValues = getIsNormalFLow() ? getMemberFormValueFromClaim(claimDetails, allPayers, HISInpatientNo, pathname) : {};
      const billDetailFormValues = getIsNormalFLow() ? getBillDetailFormValue(claimDetails, pathname) : {};
      const treatmentDetailFormValues = getIsNormalFLow() ? getTreatmentDetailFormValue(claimDetails, payerLookUp, uniqueIdentifier,
        getIsPageNameDetail(pathname), (getIsPageNameEdit(pathname) || getIsPageNameEditDischarge(pathname) ||
          getIsPageNameReconsider(pathname)), isNormalPayer) : {};
      const otherMemberDetailFormValues = getIsNormalFLow() ? getOtherMemberDetailFormValues(claimDetails) : {};
      const otherFormValues = !getIsNormalFLow() ? getGeneralFormValues(claimDetails, pathname, HISInpatientNo) : {};

      const { proposedLineOfTreatment, automatedProcedure, payerRoomType } = treatmentDetailFormValues;

      const dynamicFormJson = claimDetails.questions;
      if (dynamicFormJson) {
        const fieldArray = claimDetails.procedureDescAutomation ? claimDetails.procedureDescAutomation[0]?.questions : [];
        dispatch(setDynamicQuestionFields(fieldArray));
      }
      const formValues = dynamicFormJson ? {
        ...memberFormValues, ...billDetailFormValues, ...treatmentDetailFormValues, ...otherMemberDetailFormValues,
        ...otherFormValues, ...JSON.parse(dynamicFormJson),
      } : {
        ...memberFormValues, ...billDetailFormValues, ...treatmentDetailFormValues, ...otherMemberDetailFormValues,
        ...otherFormValues,
      };

      let formVal =
        isDraftSelected ? {
          ...formValues,
          ...admissionFormVal.treatmentDetail,
          ...admissionFormVal.customerDetail,
          ...admissionFormVal.billDetail,
        } : formValues;
        if (formVal.insurerName === undefined) {
          formVal = {
            ...formVal,
            ...formVal.insurerName = form?.getFieldsValue(['insurerName']),
          };
        }
        if (formVal.mobile === '') {
          formVal = {
            ...formVal,
            ...formVal.mobile = form?.getFieldsValue(['mobile']),
        }; // to set the fields when form is edited, when the values are undefined in enhancement case
      }
      const updatedFormValues = getValueBasedOnConfig(getIsPageNameDetail(pathname), formVal);
      if (isEditPage && selectedPatientDetail?.billDetails) {
        const billTotal = getBillTotal(selectedPatientDetail?.billDetails);
        updatedFormValues.claimedAmount = billTotal;
      }
      const { diagnosis = '' } = updatedFormValues;
      const icd10Field: any = form.getFieldsValue(['icd10'])['icd10'];
      const icd10FieldArray = icd10Field?.length > 0 ? icd10Field?.split(',') : [];

      const selectedDiagnosis =
        (payerLookUp[uniqueIdentifier]?.diagnosis || [])
          .filter((dia: any): any => diagnosis === dia?.payerMasterValue);

      const icd10Array = isDraftSelected ? icd10FieldArray : [selectedDiagnosis?.[0]?.payerMasterId];

      setBooleanInCard(icd10Array);
      setClaimDetailBackup(updatedFormValues);
      if (updatedFormValues?.automatedProcedure === 'Others' && (updatedFormValues?.procedure || updatedFormValues?.procedureDropDown)) {
        updatedFormValues.automatedProcedure = updatedFormValues?.procedure || updatedFormValues?.procedureDropDown;
      }
      form.setFieldsValue(updatedFormValues);
      if (payerLookUp[uniqueIdentifier]?.automationProcedures && proposedLineOfTreatment) {
        const automatedProcedures = getFilteredProcedureList(payerLookUp[uniqueIdentifier]?.automationProcedures, proposedLineOfTreatment)
          || [];
        dispatch(updateAutomatedProceduresOptions(automatedProcedures.length > 0 ?
          automatedProcedures.concat(PROCEDURE_OTHER_TYPES) : [...PROCEDURE_OTHER_TYPES]));
        if (automatedProcedure === PROCEDURE_OTHER_TYPES[0].value || automatedProcedure === PROCEDURE_OTHER_TYPES[0].name) {
          dispatch(toggleOtherProcedure(true));
        } else {
          dispatch(toggleOtherProcedure(false));
        }
      }
      if (payerRoomType) {
        dispatch(toggleOtherRoomType(true));
      }
    } else if (isDraftSelected) {
      const memberFormValues = getIsNormalFLow() ? getMemberFormValueFromClaim(claimDetails, allPayers) : {};
      const billDetailFormValues = getIsNormalFLow() ? getBillDetailFormValue(claimDetails, pathname) : {};
      const treatmentDetailFormValues = getIsNormalFLow() ? getTreatmentDetailFormValue(claimDetails, payerLookUp,
        uniqueIdentifier, getIsPageNameDetail(pathname), (getIsPageNameEdit(pathname) ||
          getIsPageNameEditDischarge(pathname) || getIsPageNameReconsider(pathname))
        , isNormalPayer) : {};
      const otherMemberDetailFormValues = getIsNormalFLow() ? getOtherMemberDetailFormValues(claimDetails) : {};
      const otherFormValues = !getIsNormalFLow() ? getGeneralFormValues(claimDetails, pathname) : {};

      const formValues = {
        ...memberFormValues, ...billDetailFormValues, ...treatmentDetailFormValues, ...otherMemberDetailFormValues,
        ...otherFormValues,
      };
      const formVal = {
        ...formValues,
        ...admissionFormVal.treatmentDetail,
        ...admissionFormVal.customerDetail,
        ...admissionFormVal.billDetail,
      };
      const updatedFormValues = getValueBasedOnConfig(getIsPageNameDetail(pathname), formVal);
      const { diagnosis: diagnosisId = [] } = updatedFormValues;

      const selectedDiagnosis =
        (payerLookUp[uniqueIdentifier]?.diagnosis || [])
          .filter((dia: any): any => diagnosisId?.includes(dia?.value));

      setBooleanInCard([selectedDiagnosis?.[0]?.payerMasterId]);

      if (admissionFormVal?.treatmentDetail?.payerRoomType) {
        dispatch(toggleOtherRoomType(true));
      }

      if (admissionFormVal?.treatmentDetail?.procedure) {
        dispatch(toggleOtherProcedure(true));
      }
    }
  }, [claimDetails, payerLookUp]);
// this useeffect will gets called when ever there is any change in automatedprocedureoptions on opening drafted claims
  useEffect((): any => {

    if (isDraftSelected && cardCollapse?.isMaternityOpen === false) {
      const icd10 = form.getFieldsValue(['icd10']) || admissionFormVal?.treatmentDetail?.icd10;
      const data = String(icd10?.icd10).split(',')[0];
      setBooleanInCard(Array(data));
    }
  }, [automatedProcedureOptions]);

  useEffect((): void => {
    if (!isEmpty(selectedPatientDetail) && !isDraftSelected) { // draft case form should not have his card details
      const formVal: any = updateHISAdmissionFormValue(
        selectedPatientDetail, admissionFormVal as AdmissionForm, payer,
      );
      // condition to check if the patient has a valid policy
      if (!isEmpty(selectedPolicyDetail) || !isEmpty(selectedBeneficiaryDetail)) {
        const doa = selectedPatientDetail?.treatmentDetails?.dateOfAdmission;
        const policyStart = selectedBeneficiaryDetail?.policyStartDate || selectedPolicyDetail?.policyStartDate;
        const policyEnd = selectedBeneficiaryDetail?.policyEndDate || selectedPolicyDetail?.policyEndDate;
        if (doa && isBefore(parseISO(doa), startOfDay(new Date(policyStart))) || isAfter(parseISO(doa), endOfDay(new Date(policyEnd)))) {
          if (!payer?.isOnMailPayer) {
            formVal.treatmentDetail.stayLength = null;
          }
        }
      }
      dispatch(setAdmissionForm(formVal));
    }
  }, [isShowAdmissionForm, selectedPatientDetail]);

  useEffect((): void => {
    if (admissionFormVal && Object.keys(admissionFormVal).length && isEmpty(claimDetails)) {
      const { customerDetail, treatmentDetail, billDetail } = admissionFormVal;
      const updatedFormValues = {...customerDetail, ...treatmentDetail, ...billDetail};

      if (!isEmpty(selectedBeneficiaryDetail)) {
        const {payerId, productCode} = selectedBeneficiaryDetail || {};
        const payerUniqueId = productCode ? `${payerId}${productCode}` : `${payerId}`;
        const { name: payerName = '' } = allPayers.find(({ uniqueIdentifier: uniqId }: Payer): boolean =>
          payerUniqueId === uniqId) || {};
        setClaimDetailBackup({ ...updatedFormValues, payer: payerName });
      }

      if ( !isEmpty(selectedBeneficiaryDetail) ||
      ( process.env.REACT_APP_IS_NHCX_ENABLED === 'true' && isNHCXEnabledProvider && !isEmpty(selectedPolicyDetail)) ||
      (isHisSearchEnabled && !isEmpty(selectedPatientDetail)) ) {
        form.setFieldsValue({healthId: selectedBeneficiaryDetail?.abhaNo ||
          selectedPolicyDetail?.abhaNo ||
          selectedPatientDetail?.patientDetails?.abhaId});
        setClaimDetailBackup({ ...updatedFormValues,
          healthId: selectedPolicyDetail?.abhaNo || selectedPatientDetail?.patientDetails?.abhaId });
      }

      if (isHisSearchEnabled && !isEmpty(selectedPatientDetail)) {
        if (!isDraftSelected) { // draft case fields shouldnt get disabled in case of HIS
          setClaimDetailBackup({ ...updatedFormValues, memberId: selectedBeneficiaryDetail?.memberId });
        } else { // payername to be disabled in any case
          setClaimDetailBackup( {payer: admissionFormVal?.customerDetail?.payer });
        }
      }
    }
  }, [admissionFormVal, selectedBeneficiaryDetail]);

  return claimDetailBackup;
};
